import { createSlice } from '@reduxjs/toolkit'
import { getTerms } from '../../../cartAndOrders/store/thunk'
import { keyBy } from '../../../helpers/common'
import { register } from '../../../helpers/reducerRegistry'
import { valByKeyWithDot } from '../../../helpers/value'
import {
  convertPricingPackages,
  convertUniquePackageTypes,
  isNotEmptyStr,
} from '../helpers'
import {
  getIsAvailableToAddNewSubscription,
  getPricingPackages,
  getSubscriptionPlanDetails,
  getSubscriptionPlans,
  getSubscriptionPricings,
  getUserSetting,
  postQuotations,
} from './thunk'

const ONE_PACKAGE = 1

export const slice = createSlice({
  name: 'userManagement/subscriptionPayment',
  initialState: {
    isLoadingPlanDetails: true,
    subscriptionPlanDetails: {
      ids: [],
      dataById: {},
      origData: [],
    },
    isLoadingPlan: true,
    subscriptionPlans: [],
    isLoadingPackage: true,
    packageList: {
      origData: [],
      ids: [],
      dataById: {},
      uniquePackageList: [],
      origPayloadData: [],
    },
    buyPackage: {
      isBuyOne: true,
      listPackage: [],
    },
    subscriptionInfo: {
      currentPlan: null,
      expiryDate: '',
      trialExpired: null,
      months: 0,
    },
    quotationForm: {},
    contractionForm: {},
    invoiceReceivingForm: {},
    subscriptionPricing: [],
    subscriptionPurchasedPrice: null,
    isShowActivationResult: false,
    cardAndTransferPaymentResult: {
      isBuyPackage: null,
      reportName: '',
      isScreening: null,
      numberReport: null,
      userType: null,
      startDate: '',
      endDate: '',
    },
    isCheckRules: false,
    terms: '',
    loadingGetTerms: false,
    isLoadingDownload: false,
    subscriptionDetailsParams: {},
    keyCheckValidVolume: true,
    listValidVolume: {},
    isAvailableToAddNewSubscription: {},
  },
  reducers: {
    setBuyOnePackage: (state, action) => {
      state.buyPackage.isBuyOne = action.payload
    },
    setListPackage: (state, action) => {
      state.buyPackage.listPackage = action.payload
    },
    setQuotationForm: (state, action) => {
      state.quotationForm = action.payload
    },
    setContractionForm: (state, action) => {
      state.contractionForm = action.payload
    },
    setInvoiceReceivingForm: (state, action) => {
      state.invoiceReceivingForm = action.payload
    },
    setSubscriptionPrice: (state, action) => {
      state.subscriptionPurchasedPrice = action.payload
    },
    setIsShowActivationResult: (state, action) => {
      state.isShowActivationResult = action.payload
    },
    changeCheckRules: (state, action) => {
      state.isCheckRules = action.payload
    },
    changeSubscriptionDetailsParams: (state, action) => {
      state.subscriptionDetailsParams = action.payload
    },
    changeKeyCheckValidVolume: (state) => {
      state.keyCheckValidVolume = !state.keyCheckValidVolume
    },
    changeListValidVolume: (state, action) => {
      state.listValidVolume = { ...state.listValidVolume, ...action.payload }
    },
  },
  extraReducers: (builder) => {
    // Subscription Plan
    builder.addCase(getSubscriptionPlans.pending, (state) => {
      state.isLoadingPlan = true
    })
    builder.addCase(getSubscriptionPlans.fulfilled, (state, action) => {
      state.isLoadingPlan = false

      const payloadData = action.payload.length ? action.payload : []
      state.subscriptionPlans = [...payloadData].reverse()
    })
    builder.addCase(getSubscriptionPlans.rejected, (state, action) => {
      state.isLoadingPlan = action.payload.loading
    })

    // Subscription Plan Details
    builder.addCase(getSubscriptionPlanDetails.pending, (state) => {
      state.isLoadingPlanDetails = true
    })
    builder.addCase(
      getSubscriptionPlanDetails.fulfilled,
      (state, { payload = [] }) => {
        state.isLoadingPlanDetails = false
        const data = payload.filter((item) => !!item?.showOnPlanDetail)

        const payloadData = data.length
          ? data.map((item) => ({
              ...item,
              basic: isNotEmptyStr(item.displayTextBasic)
                ? item.displayTextBasic
                : item.basic,
              lite: isNotEmptyStr(item.displayTextLite)
                ? item.displayTextLite
                : item.lite,
              standard: isNotEmptyStr(item.displayTextStandard)
                ? item.displayTextStandard
                : item.standard,
              premium: isNotEmptyStr(item.displayTextPremium)
                ? item.displayTextStandard
                : item.premium,
            }))
          : []

        state.subscriptionPlanDetails.ids = payloadData.map((item) => item.id)
        state.subscriptionPlanDetails.dataById = keyBy(payloadData, 'id')
        state.subscriptionPlanDetails.origData = keyBy(payloadData, 'id')
      },
    )
    builder.addCase(getSubscriptionPlanDetails.rejected, (state, action) => {
      state.isLoadingPlanDetails = action.payload.loading
    })

    // Package List
    builder.addCase(getPricingPackages.pending, (state) => {
      state.isLoadingPackage = true
    })
    builder.addCase(getPricingPackages.fulfilled, (state, action) => {
      const userTypeId = action.meta.arg?.userType || null

      state.isLoadingPackage = false
      const payloadData = action.payload.length
        ? action.payload.map((item) => ({
            ...item,
            reportTypeId:
              item.quantity === ONE_PACKAGE
                ? item.fiinGateReportTypeId
                : `${item.fiinGateReportTypeId}_${item.quantity}`,
            reportName: item.fiinGateReportTypeName,
            packageId: item.dataPackageId,
            name:
              item.quantity === ONE_PACKAGE
                ? item.fiinGateReportTypeName
                : item.quantity,
            isReportNameRow: item.quantity === ONE_PACKAGE,
            parentId: item.fiinGateReportTypeId,
            value: item.discountPercentageByQuantity,
          }))
        : []

      const data = convertPricingPackages(payloadData)

      state.packageList.ids = data.map((item) => item.id)

      state.packageList.dataById = keyBy(data, 'id')
      state.packageList.origData = data
      state.packageList.origPayloadData = payloadData

      const dataByUserType = payloadData.filter(
        (item) => item.packageId === userTypeId,
      )
      state.packageList.uniquePackageList = convertUniquePackageTypes(
        dataByUserType,
        'price',
        'isReportNameRow',
        'fiinGateReportTypeId',
      )
    })
    builder.addCase(getPricingPackages.rejected, (state, action) => {
      state.isLoadingPackage = action.payload.loading
    })

    //Subscription Info
    builder.addCase(getUserSetting.fulfilled, (state, action) => {
      state.subscriptionInfo.currentPlan =
        action.payload?.dataPackageCode || null
      state.subscriptionInfo.expiryDate = action.payload?.endDate || ''
      state.subscriptionInfo.trialExpired = action.payload?.trialExpired || null
      state.subscriptionInfo.months = action.payload?.months || null
    })

    //Subscription Pricing
    builder.addCase(getSubscriptionPricings.fulfilled, (state, action) => {
      state.subscriptionPricing = action.payload
    })

    //getTerms
    builder.addCase(getTerms.pending, (state) => {
      state.loadingGetTerms = true
    })
    builder.addCase(getTerms.fulfilled, (state, { payload = {} }) => {
      state.terms = payload
      state.loadingGetTerms = false
    })
    builder.addCase(getTerms.rejected, (state, action) => {
      state.loadingGetTerms = false
    })

    //postQuotations
    builder.addCase(postQuotations.pending, (state) => {
      state.isLoadingDownload = true
    })
    builder.addCase(postQuotations.fulfilled, (state) => {
      state.isLoadingDownload = false
    })
    builder.addCase(postQuotations.rejected, (state) => {
      state.isLoadingDownload = false
    })

    // getIsAvailableToAddNewSubscription
    builder.addCase(
      getIsAvailableToAddNewSubscription.fulfilled,
      (state, action) => {
        state.isAvailableToAddNewSubscription = action.payload
      },
    )
  },
})

export const selectSubscriptionPlanDetails = (state) =>
  state[slice.name].subscriptionPlanDetails
export const selectSubscriptionPlanDetailsIds = (state) =>
  state[slice.name].subscriptionPlanDetails.ids
export const selectSubscriptionPlanDetailsData = (state) =>
  state[slice.name].subscriptionPlanDetails.origData
export const selectSubscriptionPlanDetailsById = (id, attr) => (state) =>
  valByKeyWithDot(state[slice.name].subscriptionPlanDetails.dataById[id], attr)
export const selectLoadingPlanDetails = (state) =>
  state[slice.name].isLoadingPlanDetails
export const selectSubscriptionPlans = (state) =>
  state[slice.name].subscriptionPlans
export const selectLoadingPlan = (state) => state[slice.name].isLoadingPlan

export const selectPackageListIds = (state) => state[slice.name].packageList.ids
export const selectPackageListById = (id, attr) => (state) =>
  valByKeyWithDot(state[slice.name].packageList.dataById[id], attr)
export const selectPackageListData = (state) =>
  state[slice.name].packageList.origData
export const selectLoadingPackageList = (state) =>
  state[slice.name].isLoadingPackage
export const selectUniquePackageTypes = (state) =>
  state[slice.name].packageList.uniquePackageList
export const selectPackageListOrigData = (state) =>
  state[slice.name].packageList.origPayloadData

export const selectBuyPackage = (state) => state[slice.name].buyPackage

export const selectSubscriptionInfo = (state) =>
  state[slice.name].subscriptionInfo

export const selectQuotationForm = (state) => state[slice.name].quotationForm
export const selectContractionForm = (state) =>
  state[slice.name].contractionForm
export const selectInvoiceReceivingForm = (state) =>
  state[slice.name].invoiceReceivingForm

export const selectSubscriptionPricing = (state) =>
  state[slice.name].subscriptionPricing

export const selectSubscriptionPurchasedPrice = (state) =>
  state[slice.name].subscriptionPurchasedPrice

export const selectCardAndTransferPaymentResult = (state) =>
  state[slice.name].cardAndTransferPaymentResult

export const selectIsShowActivationResult = (state) =>
  state[slice.name].isShowActivationResult

export const selectCheckRules = (state) => state[slice.name].isCheckRules
export const selectTerms = (state) => state[slice.name].terms
export const selectIsLoadingDownload = (state) =>
  state[slice.name].isLoadingDownload
export const selectSubscriptionDetailsParams = (state) =>
  state[slice.name].subscriptionDetailsParams
export const selectKeyCheckValidVolume = (state) =>
  state[slice.name].keyCheckValidVolume
export const selectListValidVolume = (state) =>
  state[slice.name].listValidVolume
export const selectIsAvailableToAddNewSubscription = (state) =>
  state[slice.name].isAvailableToAddNewSubscription

export const {
  setBuyOnePackage,
  setListPackage,
  setQuotationForm,
  setContractionForm,
  setInvoiceReceivingForm,
  setSubscriptionPrice,
  setIsShowActivationResult,
  changeCheckRules,
  changeSubscriptionDetailsParams,
  changeKeyCheckValidVolume,
  changeListValidVolume,
} = slice.actions

register(slice.name, slice.reducer)
